import { Box, Tab, Tabs, useTheme } from "@mui/material";
import { NavLink, useLocation } from "react-router-dom";

import { useHasPermission, useIsSystem } from "../../contexts/PermissionsProvider";
import { DIRECTORY_TAB_LABELS } from "../../helpers/constants";

/**
 * DirectoryNavRail — horizontal MUI Tabs sub-nav wired to react-router.
 *
 * The Directory surface holds Accounts (SYSTEM-only), Users (label
 * swaps to "Members" for ACCOUNT_ADMIN callers), Groups (both), and
 * Roles (both, preview). This rail is filtered by the caller's role
 * client-side; the routes themselves are additionally gated in
 * App.js via <ProtectedRoutes requiredRole={...}> so the tab list can
 * never leak a surface the router will refuse.
 */

function PreviewPip() {
  const theme = useTheme();
  const bg =
    theme.palette.mode === "dark"
      ? theme.palette.grey[800]
      : theme.palette.grey[200];
  const fg =
    theme.palette.mode === "dark"
      ? theme.palette.grey[300]
      : theme.palette.grey[700];
  return (
    <Box
      component="span"
      sx={{
        ml: 1,
        px: 0.75,
        py: 0.1,
        borderRadius: 0.5,
        fontSize: 9.5,
        fontWeight: 700,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        background: bg,
        color: fg,
        lineHeight: 1.4,
      }}
    >
      preview
    </Box>
  );
}

export default function DirectoryNavRail() {
  const location = useLocation();
  // Accounts sub-tab is cross-tenant admin territory — same entitlement
  // used by constants.js NAV_ITEMS to gate the Accounts sub-item. The
  // "SYSTEM" verb in the list is the wildcard fallback for platform
  // super-users whose snapshot may not carry the specific key. Post
  // PR-1.5f we no longer gate on role-name strings like the old
  // SYSTEM_DIRECTORY_ADMIN — those churn under V88's DIRECTORY_ADMIN
  // rename, while entitlement keys are stable across it.
  const canSeeAccounts = useHasPermission([
    "SYSTEM",
    "directory.account.manage",
  ]);
  // isSystem drives the cross-tenant label swap ("Users" vs "Members").
  // Sourced from `useIsSystem()` — which checks the JWT-decoded
  // `userType === "SYSTEM"` first, then the SYSTEM wildcard or the
  // `directory.account.manage` entitlement in the reactive snapshot —
  // so a SYSTEM caller can never be misclassified as ACCOUNT_ADMIN
  // just because the /api/me/permissions payload didn't echo the
  // synthetic SYSTEM verb (see PermissionsProvider for the full note).
  const isSystem = useIsSystem();
  const labels = isSystem
    ? DIRECTORY_TAB_LABELS.system
    : DIRECTORY_TAB_LABELS.account;

  const tabs = [
    ...(canSeeAccounts
      ? [{ label: labels.accounts, to: "/directory/accounts", ship: true }]
      : []),
    { label: labels.users, to: "/directory/users", ship: true },
    { label: labels.groups, to: "/directory/groups", ship: true },
    { label: labels.roles, to: "/directory/roles", ship: false },
  ];

  // Match the most-specific prefix so /directory/users/anything still
  // highlights Users.
  const active =
    [...tabs]
      .sort((a, b) => b.to.length - a.to.length)
      .find((t) => location.pathname.startsWith(t.to))?.to || tabs[0]?.to;

  return (
    <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
      <Tabs
        value={active}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Directory sub-navigation"
      >
        {tabs.map((t) => (
          <Tab
            key={t.to}
            value={t.to}
            component={NavLink}
            to={t.to}
            label={
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                {t.label}
                {!t.ship && <PreviewPip />}
              </span>
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              letterSpacing: "0.01em",
              minHeight: 44,
              "&.active": { color: "primary.main" },
            }}
          />
        ))}
      </Tabs>
    </Box>
  );
}
